import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tagService } from '../services/TagService';
import { ValueSetWrapper, WrappedValueSet } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { TagFilters } from '../types';
import { ValueSet } from 'src/careflow/ph-core/fhir-client/resources/resources';

export function useTags(filters?: TagFilters, search?: string) {
  return useQuery<WrappedValueSet[]>({
    queryKey: ['tags', filters, search],
    queryFn: async (): Promise<WrappedValueSet[]> => {
      const tags = await tagService.getTags({ search });
      // Ensure each tag is properly wrapped with ValueSetWrapper
      const wrappedTags = tags
        .filter((tag) => tag && tag.resourceType === 'ValueSet')
        .map((tag): WrappedValueSet => {
          try {
            return ValueSetWrapper(tag);
          } catch (error) {
            console.error('Error wrapping tag:', error, tag);
            // Return wrapped tag even if error - wrapper should still work
            return ValueSetWrapper(tag);
          }
        });
      return wrappedTags;
    },
    enabled: true, // Always enable the query
    retry: 1, // Only retry once on failure
    staleTime: 0, // Always consider data stale to ensure fresh data
    refetchOnWindowFocus: true, // Refetch when window regains focus
    refetchOnMount: true, // Always refetch on mount
  });
}

export function useTag(id: string | null) {
  return useQuery({
    queryKey: ['tag', id],
    queryFn: () => tagService.getTagById(id!),
    enabled: !!id,
  });
}

export function useCreateTag() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: tagService.createTag,
    onSuccess: (data) => {
      console.log('Tag created successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
    onError: (error: any) => {
      console.error('Create tag mutation error:', error);
    },
  });
}

export function useUpdateTag() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, tag }: { id: string; tag: any }) =>
      tagService.updateTag(id, tag),
    onSuccess: (data) => {
      console.log('Tag updated successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

export function useDeleteTag() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: tagService.deleteTag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

